/*
 * Model for Tenants
 */
define([
  "backbone",
  "jquery",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, $, Messenger, Properties, SortableListModel) {
  var TenantModel = {};

  TenantModel.AUTHENTICATION_METHOD = Object.freeze({
    BUILT_IN: "BUILT_IN",
    COGNITO_SSO: "COGNITO_SSO",
    EMPLIFI_SUITE: "EMPLIFI_SUITE",
  });

  const AUTH_METHOD_I18N_PREFIX = "admin.tenantAdmin.authenticationMethod.";

  TenantModel.AUTHENTICATION_METHOD_I18N_KEYS = Object.fromEntries(
    Object.entries(TenantModel.AUTHENTICATION_METHOD).map(([key, value]) => [
      key,
      AUTH_METHOD_I18N_PREFIX + value,
    ]),
  );

  TenantModel.AUTHENTICATION_METHOD_OPTIONS = Object.entries(TenantModel.AUTHENTICATION_METHOD).map(
    ([key, value]) => ({
      value,
      i18nKey: TenantModel.AUTHENTICATION_METHOD_I18N_KEYS[key],
    }),
  );

  TenantModel.DEFAULT_AUTHENTICATION_METHOD = TenantModel.BUILT_IN;

  TenantModel.tenant = Backbone.Model.extend({
    idAttribute: "tenantId",
    defaults: {
      tenantName: "",
      tenantDesc: "",
      analyticsTagSessionAttribute: "",
      authenticationMethod: TenantModel.DEFAULT_AUTHENTICATION_METHOD,
      emplifiSuiteAccountId: "",
      kbUsage: 0,
      landlordId: 0,
      loginLockAttempts: 10,
      loginRetryTimeoutMinutes: 60,
      passwordComplexity: 3,
      passwordHistoryCount: 0,
      passwordMaxAge: 0,
      passwordMinAge: 0,
      passwordMinLength: 8,
      siteMapS3BucketName: "",
      siteMapCloudFront: "",
      siteMapBaseUrl: "",
      tableauUser: "",
    },
    defaultValidateAttributes: [
      "tenantName",
      "tenantDesc",
      "landlordId",
      "analyticsTagSessionAttribute",
    ],
    backup: function () {
      this.onSynced(function () {
        this.wasDirty = this.dirty;
        this.cloneCopy = this.clone();
      }, this);
    },
    restore: function () {
      this.set(this.cloneCopy.attributes);
      this.dirty = this.wasDirty;
    },
    _validateDuplicateTenantName: function (value) {
      if (this.collection) {
        var duplicateName = false;
        this.collection.each(function (tenant) {
          //check if the other object has the same name (ignore case) and is actually a different object
          if (
            tenant.get("tenantName").toLowerCase() === value.toLowerCase() &&
            tenant.cid !== this.cid
          ) {
            duplicateName = true;
          }
        }, this);
        if (duplicateName) return $.t("admin.tenantAdmin.duplicateNameError");
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "tenantName") {
        return (
          this.validateRequiredValue(value, { maxLength: 50 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateTenantName(value)
        );
      }
      if (attribute === "tenantDesc") {
        return this.validateStringLength(value, { maxLength: 255 });
      }
      if (attribute === "landlordId") {
        //Check if the landlord selected has any remaining capacity
        if (value !== 0) {
          const selectedLandlord = this.collection.landlords.findWhere({ landlordId: value });
          const tenantCapacity = selectedLandlord.get("tenantCapacity");
          if (tenantCapacity.usage >= tenantCapacity.capacity) {
            return $.t("admin.tenantAdmin.landlordTenantCapacityReached", {
              count: tenantCapacity.capacity,
            });
          }
          if (this.get("kbUsage") > 0) {
            if (
              selectedLandlord.get("kbCapacity").usage > selectedLandlord.get("kbCapacity").capacity
            ) {
              return $.t("admin.tenantAdmin.landlordKbCapacityReached", {
                count: selectedLandlord.get("kbCapacity").capacity,
              });
            }
          }
        }
      }
      if (attribute == "analyticsTagSessionAttribute") {
        return (
          this.validateStringLength(value, { maxLength: 255 }) ||
          this.validateSimpleCharacters(value)
        );
      }
      if (attribute == "tenantEmplifiSuiteAccountId") {
        return this.validateStringLength(value, { maxLength: 100 });
      }
    },
  });

  TenantModel.tenants = Backbone.Collection.extend({
    model: TenantModel.tenant,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "tenantName");
    },
    addItem: function () {
      var newItem = new TenantModel.tenant();
      this.add(newItem);
      return newItem;
    },
    url: function () {
      return Properties.adminRootURL + "/tenants";
    },
  });

  Messenger.reqres.setHandler("tenant:needsPassword", function () {
    const tenant = Messenger.reqres.request("active:tenant");
    return (
      !tenant || tenant.get("authenticationMethod") === TenantModel.AUTHENTICATION_METHOD.BUILT_IN
    );
  });

  Messenger.reqres.setHandler("fetch:tenant", function (tenantId) {
    if (!tenantId) {
      return null;
    }
    var tenant = new TenantModel.tenant();
    tenant.fetch({ url: Properties.adminRootURL + "/tenants/" + tenantId });
    return tenant;
  });

  Messenger.reqres.setHandler(
    "tenants",
    function () {
      var tenants = new TenantModel.tenants();
      tenants.fetch();
      return tenants;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "tenant:admin:headers",
    function () {
      var isGlobalAdmin = Messenger.reqres.request("user:is:global:admin");
      var Header = SortableListModel.headerModel;
      var headers = new Backbone.Collection([
        new Header({
          headerName: $.t("admin.tenantAdmin.tenantName"),
          modelAttribute: "tenantName",
          selectable: true,
          sortable: true,
          filterable: true,
          longTextField: true,
        }),
        new Header({
          headerName: $.t("admin.tenantAdmin.tenantDescription"),
          modelAttribute: "tenantDesc",
          longTextField: true,
          sortable: true,
          hideable: true,
          filterable: false,
        }),
        new Header({
          headerName: $.t("admin.tenantAdmin.kbUsage"),
          modelAttribute: "kbUsage",
          sortable: true,
          filterable: false,
        }),
      ]);
      // Add the landlord column for global admin users
      if (isGlobalAdmin && Properties.showLandlords) {
        headers.add([
          new Header({
            headerName: $.t("admin.tenantAdmin.landlord"),
            modelAttribute: "_thisObject.landlordName",
            sortable: true,
            filterable: true,
            textField: true,
          }),
        ]);
      }
      headers.add([
        new Header({
          headerName: "",
          modelAttribute: "tenantName",
          width: "15px",
          customHtml:
            '<button type="button" class="btn btn-danger btn-mini deleteTenant\
          <% if (kbUsage > 0) { %> disabled" data-i18n="[title]admin.tenantAdmin.deleteTenantInUse" <% } else { %>"\
          data-i18n="[title]admin.tenantAdmin.deleteTenant" <% } %> data-toggle="tooltip">\
            <i class="icon-remove"></i></button> ',
        }),
      ]);
      return headers;
    },
    this,
  );

  TenantModel.responseTypeCapacity = Backbone.Model.extend({
    defaults: {
      usage: 0,
      capacity: 0,
    },

    initializeCustom: function (options) {
      this.tenantId = options.tenantId;
      this.fetch({ tenantId: this.tenantId });
    },

    url: function () {
      return Properties.adminRootURL + "/tenants/" + this.tenantId + "/responseTypeCapacity";
    },
  });

  Messenger.reqres.setHandler("fetch:tenant:responseTypeCapacity", function (tenantId) {
    return new TenantModel.responseTypeCapacity({ tenantId: tenantId });
  });

  return TenantModel;
});
